import { useState, useCallback, useEffect } from 'react';

interface UseModalReturn<T = any> {
  isOpen: boolean;
  data: T | null;
  openModal: (data?: T) => void;
  closeModal: () => void;
  toggleModal: () => void;
}

interface UseModalOptions {
  closeOnEscape?: boolean;
  closeOnOutsideClick?: boolean;
  onOpen?: () => void;
  onClose?: () => void;
}

/**
 * モーダル状態管理用カスタムフック
 * - 開閉状態の管理
 * - モーダルに関連するデータの管理
 * - ESCキーでの閉じる機能
 * - オープン/クローズコールバック
 */
export const useModal = <T = any>(options: UseModalOptions = {}): UseModalReturn<T> => {
  const [isOpen, setIsOpen] = useState(false);
  const [data, setData] = useState<T | null>(null);

  const {
    closeOnEscape = true,
    onOpen,
    onClose
  } = options;

  const openModal = useCallback((modalData?: T) => {
    setIsOpen(true);
    setData(modalData || null);
    onOpen?.();
  }, [onOpen]);

  const closeModal = useCallback(() => {
    setIsOpen(false);
    setData(null);
    onClose?.();
  }, [onClose]);

  const toggleModal = useCallback(() => {
    if (isOpen) {
      closeModal();
    } else {
      openModal();
    }
  }, [isOpen, openModal, closeModal]);

  // ESCキーでモーダルを閉じる
  useEffect(() => {
    if (!closeOnEscape || !isOpen) return;

    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        closeModal();
      }
    };

    document.addEventListener('keydown', handleEscapeKey);

    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [isOpen, closeModal, closeOnEscape]);

  // モーダルが開いている間のbodyスクロール防止
  useEffect(() => {
    if (isOpen) {
      const originalStyle = window.getComputedStyle(document.body).overflow;
      document.body.style.overflow = 'hidden';
      
      return () => {
        document.body.style.overflow = originalStyle;
      };
    }
  }, [isOpen]);

  return {
    isOpen,
    data,
    openModal,
    closeModal,
    toggleModal
  };
};

export default useModal;